// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// A dedicated request function for handling FormData
const requestFormData = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;

  // For FormData requests, leave headers empty so that the browser sets multipart boundaries.
  // Also, do not perform any serialization on the body.
  const fetchOptions = {
    credentials: options.credentials || 'include',
    headers: options.headers || {}, // leave headers empty
    ...options,
  };

  return fetch(url, fetchOptions).then(response => response.json());
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

//get

const get = path => {
  const requestOptions = {
    method: methods.GET,
  };

  return request(path, requestOptions);
};

/**
 * Makes a POST request to the local API endpoint with a FormData body.
 *
 * @param {string} path - the path of the API endpoint
 * @param {FormData} formData - the form data to send with the request
 * @param {Object} [options] - additional options to pass to the request
 * @returns {Promise} resolves to the response of the request
 */
const postFormData = (path, formData, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body: formData,
  };
  return requestFormData(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Mapbox
export const getReverseGeocode = ({ latitude, longitude }) => {
  return get(`/api/mapbox/get-reverse-geocode?latitude=${latitude}&longitude=${longitude}`);
};

// Stripe
export const fetchSubscriptionPrice = body => {
  return post(`/api/stripe/fetch-subscription-price`, body);
};

export const initiateStripeSubscription = body => {
  return post('/api/stripe/initiate-subscription', body);
};

export const cancelSubscription = body => {
  return post('/api/stripe/cancel-subscription', body);
};

export const updateSubscriptionPlan = body => {
  return post('/api/stripe/update-subscription', body);
};
export const createSubscriptionPrice = body => {
  return post('/api/stripe/create-subscription-price', body);
};

export const deleteSubscriptionPrice = body => {
  return post('/api/stripe/delete-subscription-price', body);
};
export const createStripeSubscriptionForListing = body => {
  return post('/api/stripe/create-listing-subscription', body);
};

export const createPaymentIntentAPI = body => {
  return post('/api/stripe/create-payment-intent', body);
};
export const createChatPurchaseIntent = body => {
  return post('/api/stripe/create-chat-purchase-intent', body);
};
export const cancelListingSubscription = body => {
  return post('/api/stripe/cancel-listing-subscription', body);
};

// AWS

export const uploadFileAWS = body => {
  return postFormData('/api/upload-file', body);
};
export const deleteFileAWS = body => {
  return post('/api/delete-file-aws', body);
};

// Sendgrid
export const sendEmailNotification = body => {
  return post('/api/sendgrid/send-email-notification', body);
};

// Sharetribe

export const fetchUserByEmail = body => {
  return post('/api/sharetribe/user', body);
};

export const addPerksToAdminAccount = body => {
  return post('/api/sharetribe/user/add-perks', body);
};

export const SharetribeApi = {
  /**
   * Check if a username is available in the system.
   * @param {Object} body - Object with username property.
   * @return {Promise<*>} - Resolves with a boolean indicating whether the username is available.
   */
  checkUsernameAvailability: body =>
    get(`/api/check-username-availability?username=${body.username}`),

  /**
   * Fetch listings by category.
   * @param {Object} body - Object with searchTerm and category properties.
   * @return {Promise<*>} - Resolves with an array of listings.
   */
  getListingsByCategory: body =>
    get(
      `/api/listings-by-category?searchTerm=${encodeURIComponent(
        body.searchTerm
      )}&category=${encodeURIComponent(body.category)}`
    ),
  /**
   * Fetch listings from the marketplace and update them in the database.
   * @param {Object} body - Object with authorId and subscriptionType properties.
   * @return {Promise<*>} - Resolves with a boolean indicating whether the operation was successful.
   */
  fetchAndUpdateListings: body => post('/api/fetch-and-update-listings', body),

  /**
   * Fetch user by email
   *
   * @param {Object} body - The request body
   * @return {Promise} - The promise resolved by the API
   */
  fetchTalentByEmail: body => get(`/api/fetch-user?email=${body.email}`),

  /**
   * Link talents to a listing.
   * @param {Object} body - The request body with listingId and talentIds properties.
   * @return {Promise<*>} - Resolves with a boolean indicating whether the operation was successful.
   */
  linkTalentsToListing: body => post('/api/link-talents-to-listing', body),

  /**
   * Unlink talents from a listing.
   * @param {Object} body - The request body with listingId and talentIds properties.
   * @return {Promise<*>} - Resolves with a boolean indicating whether the operation was successful.
   */
  unlinkTalentsFromListing: body => post('/api/unlink-talent-from-listing', body),

  /**
   * Create a new listing in the database.
   * @param {Object} body - The request body with the listing details.
   * @return {Promise<*>} - Resolves with a boolean indicating whether the operation was successful.
   */
  createListing: body => {
    /**
     * The request body should contain the following properties:
     * - title: The title of the listing.
     * - authorId: The authorId of the listing.
     */
    return post('/api/create-listing', body);
  },

  updateListing: body => {
    return post('/api/update-listing', body);
  },

  /**
   * Updates the referral status on the referrer's profile after a new user signs up.
   *
   * @param {Object} body - The request body with referrerId and currentUserId properties.
   * @return {Promise<*>} - Resolves with a boolean indicating whether the operation was successful.
   */
  updateReferrerProfile: body => {
    /**
     * The request body should contain the following properties:
     * - referrerId: The ID of the user who referred the current user.
     * - currentUserId: The ID of the newly signed-up user.
     */
    return post('/api/update-referrer-profile', body);
  },

  fetchTransactionById: body => get(`/api/fetch-transaction-details?txId=${body.txId}`),
};

/**
 * The Phyllo API.
 *
 * This object contains functions for interacting with the Phyllo API.
 *
 * @readonly
 * @enum {Function}
 */
export const PhylloApi = Object.freeze({
  /**
   * Create a new user in Phyllo.
   *
   * @param {Object} body - The request body with the user details.
   * @return {Promise<*>} - Resolves with a boolean indicating whether the operation was successful.
   */
  createUser: body => post('/api/phyllo/create-user', body),

  /**
   * Generate an SDK token for the user.
   *
   * @param {Object} body - The request body with the user details.
   * @return {Promise<*>} - Resolves with a boolean indicating whether the operation was successful.
   */
  generateToken: body => post('/api/phyllo/generate-token', body),

  /**
   * Fetch the user accounts for the user.
   *
   * @param {Object} body - The request body with the user details.
   * @return {Promise<*>} - Resolves with the user accounts data.
   */
  getUserAccounts: body => get(`/api/phyllo/user-accounts/${body.userId}`),

  /**
   * Disconnect the user from the given account.
   *
   * @param {Object} body - The request body with the account details.
   * @return {Promise<*>} - Resolves with a boolean indicating whether the operation was successful.
   */
  disconnectAccount: body => get(`/api/phyllo/disconnect-account?accountId=${body.accountId}`),

  /**
   * Fetch the user data for the given account.
   *
   * @param {Object} body - The request body with the account details.
   * @return {Promise<*>} - Resolves with the user data.
   */
  getUserData: body => get(`/api/phyllo/user-data/?accountId=${body.accountId}`),
});

/**
 * The GetStream API.
 *
 * This object contains functions for interacting with the GetStream API.
 *
 * @readonly
 * @enum {Function}
 */
export const GetStreamApi = Object.freeze({
  /**
   * Create a new chat token for the user.
   *
   * @param {Object} body - The request body with the user details.
   * @return {Promise<*>} - Resolves with a boolean indicating whether the operation was successful.
   */
  createChatToken: body => post('/api/stream/create-chat-token', body),

  /**
   * Open a direct message channel for the user.
   *
   * @param {Object} body - The request body with the user details.
   * @return {Promise<*>} - Resolves with a boolean indicating whether the operation was successful.
   */
  openDirectMessage: body => post('/api/stream/open-direct-message', body),

  /**
   * Get message history from a channel.
   *
   * @param {Object} params - Query parameters { txId, channelId, limit, userId }
   * @return {Promise<Array>} - Resolves with an array of messages.
   */
  getChannelMessages: params => {
    const queryString = new URLSearchParams(params).toString();
    return get(`/api/stream/messages?${queryString}`);
  },

  /**
   * Check if a user has sent any messages in a channel.
   *
   * @param {Object} params - Query parameters { txId, channelId, userId }
   * @return {Promise<Object>} - Resolves with { hasMessages, messageCount, isFirstMessage }
   */
  checkUserMessageHistory: params => {
    const queryString = new URLSearchParams(params).toString();
    return get(`/api/stream/user-message-history?${queryString}`);
  },
  checkUserAttachmentHistory: params => {
    const queryString = new URLSearchParams(params).toString();
    return get(`/api/stream/user-attachment-history?${queryString}`);
  },
});
